import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Shield, Clock, CheckCircle, XCircle, AlertTriangle, Filter, RefreshCw, ChevronDown, ChevronUp, User, FileText } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

const ACTION_COLORS = {
  kick: "bg-yellow-500",
  mute: "bg-orange-500",
  warn: "bg-blue-500",
  temp_ban: "bg-red-400",
  ban: "bg-red-600",
  unmute: "bg-green-500",
  unban: "bg-green-600",
};

const ACTION_ICONS = {
  kick: "🚪",
  mute: "🔇",
  warn: "⚠️",
  temp_ban: "⏱️",
  ban: "🚫",
  unmute: "🔊",
  unban: "✅",
};

const ModerationLogPanel = () => {
  const { token, user } = useContext(AuthContext);
  const [log, setLog] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending"); // pending, log
  const [expandedAction, setExpandedAction] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  
  // Filters
  const [filterType, setFilterType] = useState("");
  const [filterStaff, setFilterStaff] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/moderation/log`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { action_type: filterType || undefined, staff_username: filterStaff || undefined }
        }),
        axios.get(`${API}/admin/moderation/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setLog(logRes.data.log || []);
      setStats(logRes.data.stats || {});
      setPendingActions(pendingRes.data.pending_actions || []);
    } catch (error) {
      console.error("Failed to fetch moderation data:", error);
      toast.error("Failed to load moderation data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, filterType, filterStaff]);

  const handleApprove = async (actionId) => {
    try {
      await axios.post(`${API}/moderation/approve/${actionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Action approved and executed!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve action");
    }
  };

  const handleReject = async (actionId) => {
    try {
      await axios.post(`${API}/moderation/reject/${actionId}?reject_reason=${encodeURIComponent(rejectReason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Action rejected");
      setRejectReason("");
      setExpandedAction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject action");
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "";
    if (minutes < 60) return `${minutes}m`;
    if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
    return `${Math.round(minutes / 1440)}d`;
  };

  return (
    <div className="space-y-4" data-testid="moderation-log-panel">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="retro-window p-2 text-center">
            <div className="text-2xl font-bold text-gray-700">{stats.total_actions}</div>
            <div className="text-xs text-gray-500">Total Actions</div>
          </div>
          <div className="retro-window p-2 text-center bg-green-50">
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
            <div className="text-xs text-gray-500">Approved</div>
          </div>
          <div className="retro-window p-2 text-center bg-red-50">
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
            <div className="text-xs text-gray-500">Rejected</div>
          </div>
          <div className="retro-window p-2 text-center bg-yellow-50">
            <div className="text-2xl font-bold text-yellow-600">{pendingActions.length}</div>
            <div className="text-xs text-gray-500">Pending</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab("pending")}
          className={`retro-button flex items-center gap-1 text-sm px-3 py-1 ${
            activeTab === "pending" ? "bg-yellow-500 text-white" : ""
          }`}
        >
          <AlertTriangle className="w-4 h-4" />
          Pending Approval ({pendingActions.length})
        </button>
        <button
          onClick={() => setActiveTab("log")}
          className={`retro-button flex items-center gap-1 text-sm px-3 py-1 ${
            activeTab === "log" ? "bg-blue-500 text-white" : ""
          }`}
        >
          <FileText className="w-4 h-4" />
          Action Log
        </button>
        <button
          onClick={fetchData}
          className="retro-button text-sm px-2 py-1 ml-auto"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Pending Actions Tab */}
      {activeTab === "pending" && (
        <div className="retro-window">
          <div className="retro-title-bar">
            <span>⏳ Actions Awaiting Your Approval</span>
          </div>
          <div className="p-3 bg-[#E0E0E0] max-h-[400px] overflow-y-auto">
            {loading ? (
              <p className="text-center py-4">Loading...</p>
            ) : pendingActions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>No pending actions</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingActions.map(action => (
                  <div 
                    key={action.action_id} 
                    className="retro-window bg-white"
                    data-testid={`pending-action-${action.action_id}`}
                  >
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2">
                          <span className={`w-8 h-8 rounded flex items-center justify-center text-white ${ACTION_COLORS[action.action_type]}`}>
                            {ACTION_ICONS[action.action_type]}
                          </span>
                          <div>
                            <div className="font-bold text-sm">
                              {action.action_type.toUpperCase().replace('_', ' ')}
                              {action.duration_minutes && (
                                <span className="ml-1 text-xs font-normal text-gray-500">
                                  ({formatDuration(action.duration_minutes)})
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-600">
                              <span className="text-red-600 font-bold">{action.target_username}</span>
                              <span className="mx-1">by</span>
                              <span className="text-blue-600">{action.requester_username}</span>
                              <span className="ml-1 text-gray-400">({action.requester_role})</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {new Date(action.created_at).toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                        <div className="font-bold text-gray-600">Reason:</div>
                        <p className="text-gray-700">{action.reason}</p>
                        {action.evidence && (
                          <>
                            <div className="font-bold text-gray-600 mt-2">Evidence:</div>
                            <p className="text-gray-700">{action.evidence}</p>
                          </>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => handleApprove(action.action_id)}
                          className="retro-button flex-1 bg-green-500 text-white text-xs py-1.5 flex items-center justify-center gap-1"
                          data-testid={`approve-${action.action_id}`}
                        >
                          <CheckCircle className="w-3 h-3" />
                          Approve & Execute
                        </button>
                        <button
                          onClick={() => setExpandedAction(expandedAction === action.action_id ? null : action.action_id)}
                          className="retro-button flex-1 bg-red-500 text-white text-xs py-1.5 flex items-center justify-center gap-1"
                        >
                          <XCircle className="w-3 h-3" />
                          Reject
                        </button>
                      </div>

                      {/* Reject reason input */}
                      {expandedAction === action.action_id && (
                        <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                          <input
                            type="text"
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            placeholder="Reason for rejection (optional)"
                            className="retro-input w-full text-xs mb-2"
                          />
                          <button
                            onClick={() => handleReject(action.action_id)}
                            className="retro-button bg-red-600 text-white text-xs w-full py-1"
                          >
                            Confirm Rejection
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Log Tab */}
      {activeTab === "log" && (
        <div className="retro-window">
          <div className="retro-title-bar">
            <span>📋 Moderation Action Log</span>
          </div>
          <div className="p-3 bg-[#E0E0E0]">
            {/* Filters */}
            <div className="flex gap-2 mb-3 flex-wrap">
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="retro-input text-xs"
              >
                <option value="">All Types</option>
                <option value="kick">Kick</option>
                <option value="mute">Mute</option>
                <option value="warn">Warn</option>
                <option value="temp_ban">Temp Ban</option>
                <option value="ban">Permanent Ban</option>
                <option value="unmute">Unmute</option>
                <option value="unban">Unban</option>
              </select>
              <input
                type="text"
                value={filterStaff}
                onChange={e => setFilterStaff(e.target.value)}
                placeholder="Filter by staff..."
                className="retro-input text-xs flex-1"
              />
            </div>

            {/* Log entries */}
            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {loading ? (
                <p className="text-center py-4">Loading...</p>
              ) : log.length === 0 ? (
                <p className="text-center py-4 text-gray-500">No actions logged</p>
              ) : (
                log.map(action => (
                  <div 
                    key={action.action_id}
                    className={`p-2 rounded border-l-4 text-xs ${
                      action.status === 'approved' ? 'bg-green-50 border-green-500' :
                      action.status === 'rejected' ? 'bg-red-50 border-red-500' :
                      'bg-gray-50 border-gray-400'
                    }`}
                    data-testid={`log-entry-${action.action_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-1 py-0.5 rounded text-white text-[10px] ${ACTION_COLORS[action.action_type]}`}>
                          {ACTION_ICONS[action.action_type]} {action.action_type.toUpperCase()}
                        </span>
                        <span className="text-red-600 font-bold">{action.target_username}</span>
                        <span className="text-gray-400">←</span>
                        <span className="text-blue-600">{action.requester_username}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {action.status === 'approved' && (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            {action.approved_by}
                          </span>
                        )}
                        {action.status === 'rejected' && (
                          <span className="text-red-600 flex items-center gap-1">
                            <XCircle className="w-3 h-3" />
                            {action.rejected_by}
                          </span>
                        )}
                        <span className="text-gray-400 text-[10px]">
                          {new Date(action.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="mt-1 text-gray-600 truncate" title={action.reason}>
                      {action.reason}
                    </div>
                    {action.reject_reason && (
                      <div className="mt-1 text-red-600 text-[10px]">
                        Rejection reason: {action.reject_reason}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModerationLogPanel;
